import React, { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  Text,
  VStack,
  useColorModeValue,
  SimpleGrid,
  IconButton,
  Icon,
  Divider,
  Alert,
  AlertIcon
} from "@chakra-ui/react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import toast from "react-hot-toast";
import { FaTrash, FaPlus, FaSearch, FaBox, FaStore, FaBarcode } from "react-icons/fa";
import SmartPriceInput from "./SmartPriceInput";

// ایمپورت سرویس‌های واقعی (اطمینان حاصل کنید مسیرها درست هستند)
import { createManualOrder } from "../../services/orderService";
import { getProducts } from "../../services/productService";
import { getGateways } from "../../services/gatewayService";

const ManualOrderForm = forwardRef(({ onSuccess }, ref) => {
  const queryClient = useQueryClient();
  const [skuSearchValues, setSkuSearchValues] = useState({});

  // 🎨 متغیرهای رنگی برای هماهنگی با تم روشن و تاریک
  const inputBg = useColorModeValue("gray.50", "gray.900");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.200");
  const itemBg = useColorModeValue("white", "whiteAlpha.50");
  const mutedColor = useColorModeValue("gray.500", "gray.400");
  const textColor = useColorModeValue("gray.800", "white");

  const commonInputProps = {
    bg: inputBg,
    borderRadius: "xl",
    _focus: { borderColor: "blue.400", boxShadow: "0 0 0 1px #3b82f6" },
  };

  // ==========================================
  // دریافت دیتا از سرور
  // ==========================================
  const { data: productsData } = useQuery(["products", "manual-order-form"], () =>
    getProducts({ page: 1, limit: 500 }) // لیمیت بیشتر برای پیدا کردن کالاها
  );
  const products = productsData?.data || [];

  const { data: gatewaysData } = useQuery(["gateways", "manual-order-form"], getGateways);
  const gateways = gatewaysData?.data || [];

  // ==========================================
  // فرم و فیلدها
  // ==========================================
  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      sku: "",
      gatewayId: "",
      items: [{ product: "", logicalVariantId: "", quantity: 1, unitPrice: "" }],
    },
    mode: "onChange",
  });

  const { fields, append, remove, update } = useFieldArray({ control, name: "items" });
  const watchedItems = watch("items");

  // ==========================================
  // توابع جستجو و مپ کردن محصولات
  // ==========================================
  const productById = useMemo(() => {
    const m = new Map();
    for (const p of products) m.set(p._id, p);
    return m;
  }, [products]);

  const normalizeSku = (value) => {
    if (!value) return "";
    return String(value).replace(/\s/g, "").toUpperCase();
  };

  const findProductAndVariantBySku = (skuRaw) => {
    const norm = normalizeSku(skuRaw);
    if (!norm) return null;

    for (const p of products) {
      if (!p) continue;

      const pCodeNorm = normalizeSku(p.accountingCodeNormalized || p.accountingCode);
      if (pCodeNorm && pCodeNorm === norm) {
        return { productId: p._id, logicalVariantId: "" };
      }

      const logicalVariants = Array.isArray(p.logicalVariants) ? p.logicalVariants : [];
      for (const lv of logicalVariants) {
        const lvNorm = normalizeSku(lv.accountingCodeNormalized || lv.accountingCode);
        if (lvNorm && lvNorm === norm) {
          return { productId: p._id, logicalVariantId: lv._id || "" };
        }
      }

      const gatewayRefs = Array.isArray(p.gatewayRefs) ? p.gatewayRefs : [];
      for (const ref of gatewayRefs) {
        const variants = Array.isArray(ref.variants) ? ref.variants : [];
        for (const v of variants) {
          const vNorm = normalizeSku(v.sku || v.accountingCodeNormalized || v.accountingCode);
          if (vNorm && vNorm === norm) {
            const lvId = v.logicalVariantId && v.logicalVariantId._id
              ? v.logicalVariantId._id
              : v.logicalVariantId || "";
            return { productId: p._id, logicalVariantId: lvId || "" };
          }
        }
      }
    }
    return null;
  };

  // ==========================================
  // ارسال اطلاعات
  // ==========================================
  const createMutation = useMutation((payload) => createManualOrder(payload), {
    onSuccess: () => {
      toast.success("سفارش دستی با موفقیت ثبت شد");
      queryClient.invalidateQueries(["orders"]);
      queryClient.invalidateQueries(["products"]);
      if (onSuccess) onSuccess();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err.message || "خطا در ثبت سفارش دستی");
    },
  });

  const onSubmit = async (values) => {
    const payload = {
      sku: values.sku,
      ...(values.gatewayId ? { gatewayId: values.gatewayId } : {}),
      items: (values.items || [])
        .map((it) => ({
          product: it.product,
          ...(it.logicalVariantId ? { logicalVariantId: it.logicalVariantId } : {}),
          quantity: parseInt(it.quantity, 10) || 0,
          ...(it.unitPrice !== "" && it.unitPrice != null ? { unitPrice: parseInt(it.unitPrice, 10) } : {}),
        }))
        .filter((it) => it.product && it.quantity > 0),
    };
    await createMutation.mutateAsync(payload);
  };

  // متصل کردن دکمه‌ی مودال خارجی به این فرم
  useImperativeHandle(ref, () => ({
    handleSubmit: () => {
      handleSubmit(onSubmit)();
    },
  }));

  return (
    <Box dir="rtl" pb={2}>
      <VStack spacing={6} align="stretch">
        
        {/* اطلاعات کلی سفارش */}
        <Box bg={useColorModeValue("gray.50", "whiteAlpha.50")} p={5} borderRadius="2xl" borderWidth="1px" borderColor={borderColor}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
            <FormControl isInvalid={errors.sku}>
              <FormLabel fontSize="sm" fontWeight="bold" display="flex" alignItems="center" gap={2}>
                <Icon as={FaBarcode} color="blue.500" />
                شماره سفارش (SKU)
              </FormLabel>
              <Input
                {...commonInputProps}
                dir="ltr"
                textAlign="right"
                placeholder="مثال: MAN-1001"
                {...register("sku", { required: "شماره سفارش الزامی است" })}
              />
              <FormErrorMessage>{errors.sku?.message}</FormErrorMessage>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" fontWeight="bold" display="flex" alignItems="center" gap={2}>
                <Icon as={FaStore} color="purple.500" />
                درگاه مبدأ (اختیاری)
              </FormLabel>
              <Select {...commonInputProps} placeholder="ثبت به عنوان فروش آزاد (بدون درگاه)" {...register("gatewayId")}>
                {gateways.map((g) => (
                  <option key={g._id} value={g._id}>
                    {g.name}
                  </option>
                ))}
              </Select>
              <Text fontSize="xs" color={mutedColor} mt={2} lineHeight="tall">
                در صورت انتخاب، کاهش موجودی به عنوان سفارش این درگاه ثبت شده و به <b>سایر درگاه‌ها</b> ارسال می‌شود.
              </Text>
            </FormControl>
          </SimpleGrid>
        </Box>

        <Divider borderColor={borderColor} />

        {/* لیست آیتم‌های سفارش */}
        <Box>
          <Flex justify="space-between" align="center" mb={4}>
            <Text fontWeight="black" fontSize="lg" color={textColor} display="flex" alignItems="center" gap={2}>
              <Icon as={FaBox} color="teal.500" />
              آیتم‌های سفارش
            </Text>
            <Button
              size="sm"
              leftIcon={<FaPlus />}
              colorScheme="teal"
              variant="solid"
              borderRadius="xl"
              onClick={() => append({ product: "", logicalVariantId: "", quantity: 1, unitPrice: "" })}
            >
              افزودن آیتم جدید
            </Button>
          </Flex>

          <VStack spacing={4} align="stretch">
            {fields.map((f, idx) => {
              const selectedProductId = watchedItems?.[idx]?.product;
              const product = selectedProductId ? productById.get(selectedProductId) : null;
              const logicalVariants = product?.logicalVariants || [];

              return (
                <Box 
                  key={f.id} 
                  bg={itemBg} 
                  borderWidth="1px" 
                  borderColor={borderColor} 
                  borderRadius="2xl" 
                  p={5} 
                  position="relative"
                  boxShadow="sm"
                  transition="all 0.2s"
                  _hover={{ boxShadow: "md", borderColor: "blue.200" }}
                >
                  {/* دکمه حذف آیتم در گوشه بالا */}
                  {fields.length > 1 && (
                    <IconButton
                      icon={<FaTrash />}
                      colorScheme="red"
                      variant="ghost"
                      size="sm"
                      position="absolute"
                      top={3}
                      left={3}
                      onClick={() => remove(idx)}
                      aria-label="حذف آیتم"
                      borderRadius="lg"
                    />
                  )}

                  <VStack spacing={5} align="stretch">
                    
                    {/* جستجوی سریع SKU */}
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="bold">جستجوی سریع با کد کالا (SKU)</FormLabel>
                      <HStack spacing={2}>
                        <Input
                          {...commonInputProps}
                          dir="ltr"
                          textAlign="right"
                          placeholder="کد کالا یا کد تنوع را وارد کنید..."
                          value={skuSearchValues[idx] || ""}
                          onChange={(e) => setSkuSearchValues((prev) => ({ ...prev, [idx]: e.target.value }))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const val = skuSearchValues[idx];
                              const result = findProductAndVariantBySku(val);
                              if (!result) return toast.error("محصول یا تنوعی با این SKU یافت نشد");
                              update(idx, { ...watchedItems?.[idx], product: result.productId, logicalVariantId: result.logicalVariantId || "" });
                            }
                          }}
                        />
                        <Button
                          colorScheme="blue"
                          px={6}
                          borderRadius="xl"
                          onClick={() => {
                            const val = skuSearchValues[idx];
                            const result = findProductAndVariantBySku(val);
                            if (!result) {
                              toast.error("محصول یا تنوعی با این کد یافت نشد");
                              return;
                            }
                            const current = watchedItems?.[idx] || {};
                            update(idx, {
                              ...current,
                              product: result.productId,
                              logicalVariantId: result.logicalVariantId || "",
                            });
                            toast.success("کالا با موفقیت انتخاب شد");
                          }}
                        >
                          یافتن
                        </Button>
                      </HStack>
                    </FormControl>

                    <Divider borderColor={borderColor} opacity={0.6} />

                    {/* انتخاب محصول و تنوع */}
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <FormControl isInvalid={errors.items?.[idx]?.product}>
                        <FormLabel fontSize="sm" fontWeight="bold">محصول انتخابی</FormLabel>
                        <Select
                          {...commonInputProps}
                          placeholder="انتخاب از لیست محصولات..."
                          {...register(`items.${idx}.product`, { required: "محصول الزامی است" })}
                          onChange={(e) => {
                            const val = e.target.value;
                            update(idx, { ...watchedItems?.[idx], product: val, logicalVariantId: "" });
                          }}
                        >
                          {products.map((p) => (
                            <option key={p._id} value={p._id}>
                              {p.name} {p.accountingCode ? `(${p.accountingCode})` : ""}
                            </option>
                          ))}
                        </Select>
                        <FormErrorMessage>{errors.items?.[idx]?.product?.message}</FormErrorMessage>
                      </FormControl>

                      <FormControl>
                        <FormLabel fontSize="sm" fontWeight="bold">تنوع کالا (اختیاری)</FormLabel>
                        <Select
                          {...commonInputProps}
                          placeholder="بدون تنوع (محصول ساده)"
                          {...register(`items.${idx}.logicalVariantId`)}
                          isDisabled={!product || logicalVariants.length === 0}
                        >
                          {logicalVariants.map((lv) => (
                            <option key={lv._id} value={lv._id}>
                              {lv.accountingCode || lv.accountingCodeNormalized || `تنوع ${lv._id.slice(-4)}`} 
                              {lv.stock != null ? ` (موجودی: ${lv.stock})` : ''}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                    </SimpleGrid>

                    {/* تعداد و قیمت */}
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <FormControl isInvalid={errors.items?.[idx]?.quantity}>
                        <FormLabel fontSize="sm" fontWeight="bold">تعداد کسر از انبار</FormLabel>
                        <NumberInput min={1} defaultValue={1}>
                          <NumberInputField 
                            {...commonInputProps} 
                            {...register(`items.${idx}.quantity`, { required: "تعداد الزامی است" })} 
                          />
                        </NumberInput>
                        <FormErrorMessage>{errors.items?.[idx]?.quantity?.message}</FormErrorMessage>
                      </FormControl>

                      <Controller
                        control={control}
                        name={`items.${idx}.unitPrice`}
                        render={({ field: { onChange, value } }) => (
                          <Box>
                            <SmartPriceInput
                              label="قیمت واحد (تومان - اختیاری)"
                              value={value}
                              onChange={onChange}
                            />
                          </Box>
                        )}
                      />
                    </SimpleGrid>

                  </VStack>
                </Box>
              );
            })}
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
});

ManualOrderForm.displayName = 'ManualOrderForm';
export default ManualOrderForm;